import React, { useState, useCallback } from "react";
import { Modal, ModalBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import trueIcon from "../../../assets/images/true-icon.png";
import falseIcon from "../../../assets/images/false-icon.png";
import {
  CALENDAR_FEATURE_NAME,
  CALENDAR_FEATURE_NAME_LEGACY,
  isCalendarIntegrationFeature,
  isComingSoonFeature,
} from "../../../constants/featureAccess";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function findFeature(plan, featureName) {
  if (!plan || !plan.plan_details) return false;
  const detail = plan.plan_details.find(
    (d) =>
      d.feature_name === featureName ||
      (isComingSoonFeature(featureName) && isComingSoonFeature(d.feature_name)) ||
      (isCalendarIntegrationFeature(featureName) &&
        isCalendarIntegrationFeature(d.feature_name)) ||
      (featureName === CALENDAR_FEATURE_NAME &&
        d.feature_name === CALENDAR_FEATURE_NAME_LEGACY)
  );
  return detail ? detail.status === "E" : false;
}

const FEATURE_SECTIONS = [
  {
    heading: "Scheduling",
    rows: [
      { label: "Connected Calendar/s", type: "calendar" },
      { label: "Events", type: "events" },
      { label: "Group Events", feature: "Group Events" },
      { label: "Personalize MeetOcto link", feature: "Personalize MeetOcto link" },
      { label: "Redirect to Custom Pages", feature: "Redirect to Custom Pages" },
    ],
  },
  {
    heading: "Branding & Customization",
    rows: [
      { label: "OctoLink Customization", feature: "OctoLink Customization (colors, images upload)" },
      { label: "Remove MeetOcto Branding", feature: "Removable MeetOcto Branding" },
      { label: "Promotional Banners", feature: "Promotional Banners" },
    ],
  },
  {
    heading: "Notifications & Automation",
    rows: [
      { label: "Automated Event Notifications", feature: "Automated event notifications" },
      { label: "Automated Reminders", feature: "Automated Notifications and Reminders" },
    ],
  },
  {
    heading: "Payments & Commerce",
    rows: [
      { label: "Collect Payments (PayPal, Stripe)", feature: "Collect Payments (PayPal, Stripe)" },
      { label: "Coupon Codes", feature: "Coupon Codes" },
      { label: "Upsell / Cross Sell", feature: "Upsell/ Cross Sell" },
      { label: "Marketing Tools", feature: "Marketing Tools" },
    ],
  },
  {
    heading: "Analytics & Website",
    rows: [
      { label: "Metrics and Reporting", feature: "Metrics and Reporting" },
      { label: "Add OctoLink to Website", feature: "Add OctoLink to your website" },
    ],
  },
  {
    heading: "Integrations",
    rows: [
      { label: "Google Meet", feature: "Google Meet" },
      { label: "Zoom", feature: "Zoom" },
      { label: "Microsoft Teams", feature: "Microsoft Teams" },
      { label: "Zapier", feature: "Zapier", comingSoon: true },
      { label: "Google / Office 365 / Outlook Calendar", feature: CALENDAR_FEATURE_NAME },
    ],
  },
];

function getCalendarText(plan) {
  if (!plan) return "—";
  const name = plan.plan_name?.toLowerCase() || "";
  if (name.includes("free")) return "1 calendar / user";
  if (name.includes("basic") || name.includes("standard")) return "4 calendars / user";
  if (name.includes("pro") || name.includes("scale")) return "6 calendars / user";
  return "Unlimited";
}

function getEventsText(plan) {
  if (!plan) return "—";
  const name = plan.plan_name?.toLowerCase() || "";
  if (name.includes("free")) return "1 event only";
  return "Unlimited";
}

function FeatureCell({ plan, row, planType }) {
  if (row.comingSoon || isComingSoonFeature(row.feature)) {
    return <span className="pdt-cell-text">Coming soon</span>;
  }
  if (row.type === "calendar") return <span className="pdt-cell-text">{getCalendarText(plan)}</span>;
  if (row.type === "events") return <span className="pdt-cell-text">{getEventsText(plan)}</span>;

  const name = plan?.plan_name?.toLowerCase() || "";
  const isIndividual = planType === "individual";

  // Team-only features for non-team plans
  if (!isIndividual && row.feature === "Pooled availability") {
    return <img src={trueIcon} className="pdt-icon" alt="yes" />;
  }

  const enabled = findFeature(plan, row.feature);
  return <img src={enabled ? trueIcon : falseIcon} className="pdt-icon" alt={enabled ? "yes" : "no"} />;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// 6 key highlights shown on each card — ordered by importance
const CARD_HIGHLIGHTS = [
  { label: "Connected Calendars", type: "calendar" },
  { label: "Events", type: "events" },
  { label: "Group Events", feature: "Group Events" },
  { label: "Collect Payments", feature: "Collect Payments (PayPal, Stripe)" },
  { label: "Remove MeetOcto Branding", feature: "Removable MeetOcto Branding" },
  { label: "Automated Reminders", feature: "Automated Notifications and Reminders" },
];

function PlanDetailModal({ plans = [], userPlan = {}, planPeriodType: externalPeriod = "monthly", onBuyPlan }) {
  const [isOpen, setIsOpen] = useState(false);
  const [planType, setPlanType] = useState("individual");
  const [period, setPeriod] = useState(externalPeriod);
  const navigate = useNavigate();

  const toggle = useCallback(() => setIsOpen((p) => !p), []);

  const isMonthly = period === "monthly";
  const isIndividual = planType === "individual";

  // Filter plans by type, sort by price ascending
  const filteredPlans = plans
    .filter((p) => p.plan_type === planType)
    .sort((a, b) => Number(a.monthly_price) - Number(b.monthly_price));

  // Which column index is highlighted (middle/best value)
  const bestIdx = Math.floor(filteredPlans.length / 2);

  const handleBuyNow = (plan) => {
    onBuyPlan?.(plan);
    toggle();
  };

  const getPrice = (plan) => {
    if (!plan) return "0";
    return isMonthly ? plan.monthly_price : plan.annual_price;
  };

  const isCurrent = (plan) => plan?.id === userPlan?.plan_id;

  return (
    <>
      <button type="button" className="btn btn-outline brand pdt-trigger-btn" onClick={toggle}>
        View Details
      </button>

      <Modal
        isOpen={isOpen}
        toggle={toggle}
        className="pdt-modal modal-xl"
        centered
      >
        <ModalBody className="p-0">
          <div className="pdt-wrapper">

            {/* Close */}
            <button className="pdt-close" onClick={toggle} aria-label="Close">
              ✕
            </button>

            {/* Header */}
            <div className="pdt-header">
              <h2 className="pdt-title">Choose Your Plan</h2>
              <p className="pdt-subtitle">Simple, transparent pricing that grows with you.</p>

              {/* Toggles row */}
              <div className="pdt-controls">
                {/* Individual / Team */}
                <div className="pdt-type-toggle">
                  <span className={!isIndividual ? "pdt-tab" : "pdt-tab active"} onClick={() => setPlanType("individual")}>
                    Individual
                  </span>
                  <label className="pdt-switch">
                    <input type="checkbox" checked={!isIndividual} onChange={() => setPlanType(isIndividual ? "team" : "individual")} />
                    <span className="pdt-switch-track" />
                  </label>
                  <span className={isIndividual ? "pdt-tab" : "pdt-tab active"} onClick={() => setPlanType("team")}>
                    For Organizations
                  </span>
                </div>

                {/* Monthly / Annual */}
                <div className="pdt-period-toggle">
                  <button
                    className={`pdt-period-btn${isMonthly ? " active" : ""}`}
                    onClick={() => setPeriod("monthly")}
                  >
                    Monthly
                  </button>
                  <button
                    className={`pdt-period-btn${!isMonthly ? " active" : ""}`}
                    onClick={() => setPeriod("annual")}
                  >
                    Annual
                    <span className="pdt-save-badge">Save 20%</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Plan cards */}
            <div className="pdt-cards-row">
              {filteredPlans.length === 0 && (
                <div className="pdt-loading">Loading plans…</div>
              )}
              {filteredPlans.map((plan, idx) => {
                const highlighted = idx === bestIdx;
                const current = isCurrent(plan);
                const isFree = Number(plan.monthly_price) === 0;

                return (
                  <div
                    key={plan.id}
                    className={`pdt-card${highlighted ? " pdt-card--featured" : ""}${current ? " pdt-card--current" : ""}`}
                  >
                    {highlighted && (
                      <div className="pdt-badge">⭐ Best Value</div>
                    )}
                    {current && !highlighted && (
                      <div className="pdt-badge pdt-badge--current">Your Plan</div>
                    )}

                    <div className="pdt-card-head">
                      <h3 className="pdt-plan-name">{plan.plan_name}</h3>
                      <div className="pdt-price-row">
                        <span className="pdt-currency">$</span>
                        <span className="pdt-price">{getPrice(plan)}</span>
                        {!isFree && (
                          <span className="pdt-period">/{isMonthly ? "mo" : "yr"}</span>
                        )}
                        {!isIndividual && (
                          <span className="pdt-per-user">/ user</span>
                        )}
                      </div>
                      {!isMonthly && !isFree && (
                        <p className="pdt-annual-note">Billed annually</p>
                      )}
                    </div>

                    <button
                      className={`pdt-cta${highlighted ? " pdt-cta--featured" : ""}`}
                      onClick={() => handleBuyNow(plan)}
                      disabled={current}
                    >
                      {current ? "Current Plan" : isFree ? "Get Started" : "Buy Now"}
                    </button>

                    <ul className="pdt-card-features">
                      {CARD_HIGHLIGHTS.map((row, ri) => {
                        let enabled = true;
                        let text = null;
                        if (row.type === "calendar") {
                          text = getCalendarText(plan);
                        } else if (row.type === "events") {
                          text = getEventsText(plan);
                        } else {
                          enabled = findFeature(plan, row.feature);
                        }
                        return (
                          <li key={ri} className={!enabled ? "pdt-feat-disabled" : ""}>
                            <img src={enabled ? trueIcon : falseIcon} className="pdt-icon" alt={enabled ? "yes" : "no"} />
                            <span>{text || row.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>

            {/* Compare all features link */}
            <div className="pdt-compare-bar">
              <button
                className="pdt-compare-btn"
                onClick={() => {
                  toggle();
                  navigate("/admin/plan-comparison");
                }}
              >
                View Full Feature Comparison
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            </div>

          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default PlanDetailModal;
